import pluralize from 'pluralize';

import {
  type WorkspaceImportRow,
  type WorkspaceImportsBanner,
  type WorkspaceImportsCounts,
  type WorkspaceImportsRail,
} from '~/components/v2/components/workspace/workspace-imports/workspaceImportTypes';

export type GetWorkspaceImportsHeaderParams = {
  canUpgradePlan: boolean;
  counts: WorkspaceImportsCounts;
  isCreditModel: boolean;
  isInFreePlan: boolean;
  isStoragePlanRestricted: boolean;
  isUpgradeSuccess: boolean;
  isUpgradingPlan: boolean;
  maxGb: number | null | undefined;
  plan: string | undefined;
};

export type WorkspaceImportsHeader = {
  banner: WorkspaceImportsBanner | null;
  description: string | undefined;
  rail: WorkspaceImportsRail;
  title: string;
};

const EMPTY_RAIL: WorkspaceImportsRail = {
  ariaLabel: 'Imports',
  badge: null,
  icon: 'default',
  label: 'Imports',
};

export const getWorkspaceImportsCounts = (rows: WorkspaceImportRow[]): WorkspaceImportsCounts => ({
  cancelledCount: rows.filter((row) => row.kind === 'cancelled').length,
  completedCount: rows.filter((row) => row.kind === 'completed').length,
  failedCount: rows.filter((row) => row.kind === 'failed').length,
  progressingCount: rows.filter((row) => row.kind === 'progressing').length,
  stoppedCount: rows.filter((row) => row.kind === 'stopped').length,
});

const getWorkspaceImportsBanner = ({
  canUpgradePlan,
  counts,
  isCreditModel,
  isStoragePlanRestricted,
  isUpgradingPlan,
  maxGb,
  plan,
}: GetWorkspaceImportsHeaderParams): WorkspaceImportsBanner | null => {
  const isOutOfPlan = plan === 'basic' || isStoragePlanRestricted;

  if (isUpgradingPlan || counts.stoppedCount === 0 || !isOutOfPlan) {
    return null;
  }

  return {
    canUpgrade: canUpgradePlan,
    message: isCreditModel
      ? "You're out of credits, imports stopped until your credits refresh or you upgrade your plan."
      : `You've hit your ${maxGb}GB import limit for free plans. Upgrade to continue.`,
    tracksCreditUpsell: isCreditModel,
  };
};

export type GetWorkspaceImportsRailParams = Pick<
  GetWorkspaceImportsHeaderParams,
  'counts' | 'isInFreePlan' | 'isUpgradeSuccess' | 'isUpgradingPlan'
>;

export const getWorkspaceImportsRail = ({
  counts,
  isInFreePlan,
  isUpgradeSuccess,
  isUpgradingPlan,
}: GetWorkspaceImportsRailParams): { description: string | undefined; rail: WorkspaceImportsRail; title: string } => {
  const sessionCount =
    counts.progressingCount + counts.stoppedCount + counts.failedCount + counts.cancelledCount + counts.completedCount;
  const hasUnresolvedImports = sessionCount > counts.completedCount;
  const isUpgradeInProgress = isInFreePlan && isUpgradingPlan && counts.stoppedCount > 0;
  const isUpgradeReady = !isInFreePlan && isUpgradeSuccess && counts.stoppedCount > 0;

  let description: string | undefined;
  let rail = EMPTY_RAIL;

  if (isUpgradeInProgress) {
    description = 'Your import will restart after upgrade';
    rail = {
      ...EMPTY_RAIL,
      ariaLabel: 'Upgrade In Progress',
      icon: 'stopped',
      label: 'Upgrade In Progress',
    };
  } else if (isUpgradeReady) {
    description = 'Import ready to restart';
    rail = {
      ...EMPTY_RAIL,
      ariaLabel: 'Upgrade Successful',
      icon: 'stopped',
      label: 'Upgrade Successful',
    };
  } else if (counts.stoppedCount > 0) {
    description = 'Stopped';
    rail = {
      ...EMPTY_RAIL,
      ariaLabel: 'Import Stopped',
      icon: 'stopped',
      label: 'Import Stopped',
    };
  } else if (counts.progressingCount > 0) {
    rail = {
      ...EMPTY_RAIL,
      ariaLabel: 'Imports, Importing',
      badge: { count: counts.progressingCount, kind: 'progress' },
      icon: 'spinner',
      label: 'Importing',
    };
  } else if (counts.failedCount > 0) {
    description = pluralize('error', counts.failedCount, true);
    rail = {
      ...EMPTY_RAIL,
      ariaLabel: 'Import Problem',
      badge: { count: counts.failedCount, kind: 'error' },
      label: 'Import Problem',
    };
  } else if (counts.cancelledCount > 0 && counts.completedCount === 0) {
    rail = {
      ...EMPTY_RAIL,
      ariaLabel: 'Import Cancelled',
      label: 'Import Cancelled',
    };
  } else if (counts.completedCount > 0) {
    rail = {
      ...EMPTY_RAIL,
      ariaLabel: 'Import Complete',
      icon: 'complete',
      label: 'Import Complete',
    };
  }

  return {
    description,
    rail,
    title: hasUnresolvedImports ? pluralize('import', sessionCount, true) : 'Imports',
  };
};

export const getWorkspaceImportsHeader = (params: GetWorkspaceImportsHeaderParams): WorkspaceImportsHeader => {
  const { description, rail, title } = getWorkspaceImportsRail(params);

  return {
    banner: getWorkspaceImportsBanner(params),
    description,
    rail,
    title,
  };
};
